// Libs
import React, { useState, useContext, useEffect, useRef } from "react";
import { useObserver, KeyValuePair, FieldType, Else, EditableInput, TypographyX } from "@shoothill/core";
import SignaturePad from "react-signature-pad-wrapper";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FormLabel, Box, Typography, Checkbox, FormControlLabel, Radio, FormControl, Button, Select, InputAdornment, TextField, useMediaQuery } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";
import ReactHtmlParser from "react-html-parser";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { HomeOwnerStatusEnum } from "Globals/Models/HomeOwnerStatusEnum";
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";

const TextBlock = styled.div`
    margin-bottom: 2px;
    line-height: 1.4;
`;

export const StyledButton = styled(Button)`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 31px;
    flex: 1;
    background-color: ${theme.palette.common.black};
    color: ${theme.palette.common.white};
    &.Mui-disabled {
        color: ${theme.palette.common.white};
        opacity: 0.5;
    }
    :first-of-type {
        margin-right: 30px;
        background-color: ${theme.palette.hightownBlue.light};
        color: ${theme.palette.common.white};
    }
    :hover {
        background-color: ${theme.palette.hightownBlue.light};
    }
`;

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const DeclarationView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.declarationViewModel);
    const signatureRef = useRef<SignaturePad>() as React.RefObject<SignaturePad>;
    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;
    useEffect(() => {
        viewModel.loadSignature();
    }, []);

    return useObserver(() => (
        <div onMouseUp={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            <TextBlock>
                <Typography variant={"caption"}>
                    Hightown take your privacy seriously and we will only use your personal information to process your application and to provide services you have requested from
                    us. We will not share your personal information without your consent, unless allowed to by law.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    We follow the principles of Data Protection legislation, as well as respecting people&apos;s rights to confidentiality and privacy.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    To enable us to process and verify your application we will share your personal information with third parties. We will not provide your information to any
                    other company for direct marketing purposes, and we will not sell or lease your personal information to third parties.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    Personal information which you supply to us, including information about other potential household members may be used in several ways including fraud
                    prevention. When you provide information about household members we assume that you do so with their full knowledge and consent.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>We will only ask for personal information that is appropriate to enable us to deliver our services.</Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    It is criminal offence to knowingly or recklessly make a false declaration or withhold information reasonably required in connection with your application.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>You must immediately notify Hightown if there are any changes of circumstances relevant to your application.</Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    The Data Protection Act gives you the right to apply for a copy of information we hold about yourself. You have the right to request a copy of the information
                    that we hold about you.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    If you would like a copy of some or all of your personal information, please telephone 01442 292300 or email{" "}
                    <a href="mailto:DataProtection@hightownha.org.uk">DataProtection@hightownha.org.uk</a>. There is no charge for this service so long as the request is not
                    unusually large, we will respond to you within 1 month.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    We will only hold your personal information during the period of our relationship with you and for a set period afterwards to allow us to meet our legal
                    obligations. Documents are retained in accordance with the latest Document Retention guidelines for Housing Associations published by the National Housing
                    Federation.
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    For further information about how the Ministry of Housing, Communities and Local Government (MHCLG) &ndash; Commercial Directorate, protects and secures your
                    personal data please follow the link below:
                </Typography>
            </TextBlock>
            <TextBlock>
                <Typography variant={"caption"}>
                    <a href="https://assets.publishing.service.gov.uk/media/6960b7af714f11cb49776603/MHCLG_privacy_note.pdf" target="_blank" rel="noopener noreferrer">
                        MHCLG Privacy Notice
                    </a>
                </Typography>
            </TextBlock>

            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={viewModel.model.canContact}
                    onChange={(e) => {
                        viewModel.handleCanContactChange(e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    I/we consent to be contacted further
                </Typography>
            </Row>

            {viewModel.model.canContact && (
                <Box mb={2}>
                    <Typography variant="body1" style={{ marginBottom: "8px" }}>
                        Please select your preferred contact methods:
                    </Typography>
                    {(
                        [
                            { field: "contactByEmail", label: "Email" },
                            { field: "contactByPhone", label: "Phone calls" },
                            { field: "contactBySms", label: "SMS / text messages" },
                            { field: "contactByPost", label: "Post / direct mail" },
                        ] as const
                    ).map(({ field, label }) => (
                        <Row key={field} style={{ alignItems: "center", flexDirection: "row" }}>
                            <Checkbox
                                checked={viewModel.model[field]}
                                onChange={(e) => viewModel.handleCheckboxChange(field, e.target.checked)}
                                icon={<CheckboxSVG />}
                                checkedIcon={<CheckboxCheckedSVG />}
                                disabled={props.applicationFormViewModel.model.isSubmitted}
                            />
                            <Typography variant="body1" style={{ marginLeft: "30px" }}>
                                {label}
                            </Typography>
                        </Row>
                    ))}
                </Box>
            )}

            <Row>
                <FormControl component="fieldset">
                    <FormLabel component="legend" style={isMobile ? { marginBottom: "10px" } : { marginBottom: "5px" }}>
                        <span style={isMobile ? { fontSize: "16px", textTransform: "uppercase" } : {}}>Signature</span>
                    </FormLabel>
                    {viewModel.getError("signature") !== "" && (
                        <p style={{ color: theme.palette.error.main, position: "absolute", fontSize: "14px" }}>{viewModel.getError("signature")}</p>
                    )}

                    <>
                        <div style={{ maxHeight: "350px", maxWidth: "500px" }}>
                            {viewModel.signatureURL == "" ? (
                                <SignaturePad
                                    ref={signatureRef}
                                    options={{
                                        backgroundColor: "rgb(255,255,255)",
                                        onEnd: () => viewModel.handleSignatureChange(signatureRef!.current!.toDataURL()),
                                    }}
                                />
                            ) : (
                                <div style={isMobile ? { width: "100%" } : { maxHeight: "350px", maxWidth: "500px" }}>
                                    <img style={{ width: "100%", height: "100%" }} src={viewModel.signatureURL} />
                                </div>
                            )}
                        </div>
                        {viewModel.IsLoading ||
                            props.applicationFormViewModel.model.isSubmitted ||
                            (storesContext.Domain.SaveStore.isSaving == false && (
                                <Row style={{ marginTop: "0px" }}>
                                    <StyledButton
                                        onClick={() => viewModel.saveSignature()}
                                        disabled={viewModel.signatureURL != ""}
                                        style={isMobile ? { width: "100%", margin: "20px 0" } : {}}
                                    >
                                        Add
                                    </StyledButton>
                                    <StyledButton
                                        onClick={() => {
                                            signatureRef.current ? signatureRef.current.clear() : null;
                                            viewModel.handleSignatureChange("");
                                        }}
                                    >
                                        Clear
                                    </StyledButton>
                                </Row>
                            ))}
                    </>
                </FormControl>

                <CustomDatePicker
                    viewModel={viewModel}
                    label="Date"
                    placeholder={"Enter date"}
                    fieldName="signedDate"
                    value={viewModel.model.signedDate}
                    onChange={(date) => {
                        viewModel.restartTimer();
                        viewModel.setDate("signedDate", date);
                    }}
                    resetCallback={viewModel.restartTimer}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
        </div>
    ));
};
